import React from 'react'
import MuiTextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Slider from 'rc-slider'
import 'rc-slider/assets/index.css'
import moment from 'moment'
import BaseDisplayItem, {
    BaseDisplayItemProps,
    DisplayItemBase,
} from '@/feature/DisplayItems/BaseDisplayItem'
import DateField from '@/feature/DisplayItems/DateField/DateField'
import DateTimeField from '@/feature/DisplayItems/DateTimeField/DateTimeField'
import DecimalField from '@/feature/DisplayItems/DecimalField'
import {
    serializeDateTimeValue,
    serializeDateValue,
} from '@/feature/DisplayItems/shared/date'

/* ---------- Types ---------- */

type SliderSearchValue = {
    custom: 'slider'
    min: number
    max: number
}

type CalcValue = string | SliderSearchValue | number[] | null
type CalcOutput = 'text' | 'date' | 'datetime' | 'integer' | 'decimal' | ''

interface CalcSettings extends Record<string, unknown> {
    show_slider?: boolean
    max_slider?: number
}

interface CalcItem extends DisplayItemBase {
    settings?: CalcSettings
}

interface Props extends BaseDisplayItemProps<CalcValue, CalcItem> {
    onKeyPress?: (
        id: string | number,
        e: React.KeyboardEvent<HTMLInputElement>
    ) => void
}

const CALC_DATE_PARSE_FORMATS = [
    moment.ISO_8601,
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DD HH:mm',
    'YYYY-MM-DD',
    'DD-MM-YYYY HH:mm:ss',
    'DD-MM-YYYY HH:mm',
    'DD-MM-YYYY',
]

function parseCalcDateTime(raw: string): moment.Moment | null {
    const trimmed = raw.trim()
    if (!trimmed) return null

    const parsed = moment(trimmed, CALC_DATE_PARSE_FORMATS, true)
    return parsed.isValid() ? parsed : null
}

function toDateFieldValue(raw: string): string | null {
    const parsed = parseCalcDateTime(raw)
    return parsed ? serializeDateValue(parsed) : null
}

function toDateTimeFieldValue(raw: string): string | null {
    const parsed = parseCalcDateTime(raw)
    return parsed ? serializeDateTimeValue(parsed) : null
}

/* ---------- Component ---------- */

class CalcField extends BaseDisplayItem<CalcValue, CalcItem, HTMLInputElement> {
    constructor(props: Props) {
        super(props)

        this.state = {
            value: props.value ?? null,
        }

        this.onSliderChange = this.onSliderChange.bind(this)
    }

    private getOutput(): CalcOutput {
        return String(
            this.props.item.def_dossier_def_field?.settings?.output ??
                this.props.item.settings?.output ??
                ''
        ).toLowerCase() as CalcOutput
    }

    private getFormattedItem(output: CalcOutput): CalcItem {
        const props = this.props as Props
        const settings = {
            ...(props.item.def_dossier_def_field?.def_field?.settings ?? {}),
            ...(props.item.def_dossier_def_field?.settings ?? {}),
            ...(props.item.settings ?? {}),
        }

        if (output === 'integer') {
            return {
                ...props.item,
                settings: {
                    ...settings,
                    format: 'number',
                    decimal_places: 0,
                    decimals: 0,
                },
            }
        }

        return {
            ...props.item,
            settings,
        }
    }

    private changeValue(value: CalcValue) {
        const props = this.props as Props
        const changeValue = props.changeValue as
            | ((id: CalcItem['id'], value: CalcValue, item?: CalcItem) => void)
            | undefined

        changeValue?.(props.item.id, value, props.item)
    }

    private onSliderChange(value: number[]) {
        this.setState({ value })

        const searchValue: SliderSearchValue = {
            custom: 'slider',
            min: value[0],
            max: value[1],
        }

        // preserve legacy behavior
        if (value[0] === 0 && value[1] === 1) {
            this.changeValue(null)
            return
        }

        this.changeValue(searchValue)
    }

    /* ---------- Render: SHOW ---------- */

    renderShow(): React.ReactNode {
        if (
            this.state.value === null ||
            this.state.value === undefined ||
            this.state.value === ''
        ) {
            return null
        }

        const props = this.props as Props
        const output = this.getOutput()
        const formattedItem = this.getFormattedItem(output)
        const showValue = String(this.state.value)

        if (output === 'date') {
            const dateValue = toDateFieldValue(showValue)
            if (!dateValue) return showValue

            return (
                <DateField
                    kedo={props.kedo}
                    item={formattedItem}
                    value={dateValue}
                    mode="show"
                />
            )
        }

        if (output === 'datetime') {
            const dateTimeValue = toDateTimeFieldValue(showValue)
            if (!dateTimeValue) return showValue

            return (
                <DateTimeField
                    kedo={props.kedo}
                    item={formattedItem}
                    value={dateTimeValue}
                    mode="show"
                />
            )
        }

        if (output === 'decimal' || output === 'integer') {
            return (
                <DecimalField
                    kedo={props.kedo}
                    item={formattedItem}
                    value={showValue}
                    mode="show"
                />
            )
        }

        return showValue
    }

    /* ---------- Render: EDIT ---------- */
    // Not used in legacy behavior; keep consistent with old component (only search had UI)
    renderEdit(): React.ReactNode {
        return this.renderShow()
    }

    /* ---------- Render: SEARCH ---------- */

    renderSearch(): React.ReactNode {
        const props = this.props as Props
        const maxSlider = props.item.settings?.max_slider ?? 1000

        if (props.item.settings?.show_slider) {
            const isRange = Array.isArray(this.state.value)
            const rangeValue = isRange ? (this.state.value as number[]) : [0, 0]

            return (
                <Box>
                    {isRange ? (
                        <Typography
                            variant="body2"
                            align="center"
                            sx={{ mb: 1 }}
                        >
                            {rangeValue[0]} - {rangeValue[1]}
                        </Typography>
                    ) : null}

                    <Slider
                        range
                        allowCross={false}
                        min={0}
                        max={maxSlider}
                        defaultValue={[0, 0]}
                        onChange={this.onSliderChange}
                    />
                </Box>
            )
        }

        return (
            <MuiTextField
                size="small"
                fullWidth
                type="text"
                value={props.value ?? ''}
                onChange={(e) => this.changeValue(e.target.value)}
                onKeyPress={
                    props.onKeyPress
                        ? (e) =>
                              props.onKeyPress?.(
                                  props.item.id,
                                  e as React.KeyboardEvent<HTMLInputElement>
                              )
                        : undefined
                }
            />
        )
    }
}

export default CalcField
